import { faBan, faCircleCheck, faHistory, faPenToSquare, faTrash, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/Stack';
import { Tab, Tabs } from 'src/components/Tabs';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import {
  Appointment,
  AppointmentStatus,
  WaitListEntry,
} from 'src/models/appointment/_types';
import {
  createAppointmentVisit,
  createAppointmentWaitListEntry,
  deleteAppointment,
  deleteWaitListEntry,
  updateAppointment,
} from 'src/models/appointment/appointment';
import { ClientSearchResult, User } from 'src/models/user/_types';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import { PaymentDialog } from 'src/pages/Clients/PaymentDialog/PaymentDialog';
import { MessageDialog } from 'src/shared/Dialogs/MessageDialog';
import { VersionHistory } from 'src/shared/VersionHistory';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import { EditEventDialog } from '../EditEventDialog';

import { BulkActions } from './BulkActions';
import { RecurringMembershipDialog } from './RecurringMembershipDialog';
import { SessionSummary } from './SessionSummary';
import { VisitAddCardDialog } from './VisitAddCardDialog';
import { VisitNotesDialog } from './VisitNotesDialog';
import { VisitsSection } from './VisitsSection';
import { WaitlistSection } from './WaitlistSection';
import { useVisitActions } from './useVisitActions';

import type { PendingVisit } from './_types';

// Recurring cancel scope - matches v2 modal-new-appointment.js lines 1188-1248
type RecurringCancelScope = 'this' | 'all';

interface ViewEventDialogProps {
  open: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  isLoading?: boolean;
  onRefresh?: () => void;
  onAppointmentUpdate?: (appointment: Appointment) => void;
}

/**
 * ViewEventDialog - Read-only view of a session with visit/waitlist management
 *
 * This is the "view mode" equivalent of v2's modal-new-appointment.hbs (lines 382-1210).
 * Separated from EditEventDialog for cleaner UX flow:
 *   NewEventDialog (service selection) → EditEventDialog (create/edit) → ViewEventDialog (manage)
 *
 * Visit flow matches v2 (modal-new-appointment.js lines 1587-1617):
 * 1. User selects client from autocomplete
 * 2. Pending visit is created locally, eligibility checked
 * 3. User must click "Save to this session" to persist
 */
export const ViewEventDialog: FC<ViewEventDialogProps> = ({
  open,
  onClose,
  appointment,
  isLoading = false,
  onRefresh,
  onAppointmentUpdate,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'schedules.calendar.viewEvent' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { canManageAppointment, canCancelVisit } = usePermissionCheck();
  const queryClient = useQueryClient();

  // Pending visits that haven't been saved yet (v2 brandNew pattern)
  const [pendingVisits, setPendingVisits] = useState<PendingVisit[]>([]);

  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Active tab (visits or waitlist)
  const [activeTab, setActiveTab] = useState(0);

  // Confirm dialogs
  const [confirmDialogProps, confirm] = useConfirmDialogComponent();

  // New dialog states for visit actions
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [recurringMembershipDialogOpen, setRecurringMembershipDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentItemType, setPaymentItemType] = useState<'package' | 'product'>('product');
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);

  // Version history dialog state
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionHistoryItemType, setVersionHistoryItemType] = useState('');
  const [versionHistoryItemId, setVersionHistoryItemId] = useState<Identifier>('');

  // Visit actions hook - provides all visit mutation handlers
  const visitActions = useVisitActions({
    appointment: appointment || ({} as Appointment),
    onVisitUpdate: (updatedVisit) => {
      // Update the appointment's visits array with the updated visit (preserves order)
      if (appointment && onAppointmentUpdate) {
        const updatedVisits = (appointment.visits || []).map((v) =>
          v.id === updatedVisit.id ? updatedVisit : v
        );
        onAppointmentUpdate({ ...appointment, visits: updatedVisits });
      }
    },
    onAppointmentRefresh: onRefresh,
    // Confirmation for fee-charging actions (matches v2 updateVisitsWithConfirmation)
    confirmFeeCharge: async (message: string) => {
      return confirm({ title: t('confirmFeeCharge'), message });
    },
    // Confirmation for delete/remove actions
    confirmDelete: async (message: string) => {
      return confirm({ title: t('confirmRemove'), message, confirmColor: 'error' });
    },
  });

  // Recurring cancel prompts - v2 lines 1188-1248
  const [cancelScopeDialogOpen, setCancelScopeDialogOpen] = useState(false);
  const [cancelNotifyDialogOpen, setCancelNotifyDialogOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<RecurringCancelScope>('this');

  // Check if this is a recurring session
  const isRecurring = !!appointment?.scheduledAppointmentId;

  // Get unique users from active (non-cancelled) visits for Message All
  const getMessageAllUsers = () => {
    const visits = appointment?.visits || [];
    const activeVisits = visits.filter(
      (v) =>
        v.userId &&
        v.status !== 'cancelled' &&
        v.status !== 'late_cancelled'
    );
    // Deduplicate by userId
    const seenIds = new Set<string>();
    return activeVisits
      .filter((v) => {
        const id = String(v.userId);
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      })
      .map((v) => ({
        id: v.userId!,
        nickname: v.userName || '',
      }));
  };
  const messageAllUsers = getMessageAllUsers();

  const handleMessageAll = () => {
    if (messageAllUsers.length > 0) {
      setMessageDialogOpen(true);
    }
  };

  // Clear state when dialog closes or appointment changes
  useEffect(() => {
    if (!open) {
      setPendingVisits([]);
      setActiveTab(0);
      setCancelScopeDialogOpen(false);
      setCancelNotifyDialogOpen(false);
      setCancelScope('this');
      // Clear new dialog states
      setSelectedVisit(null);
      setAddCardDialogOpen(false);
      setNotesDialogOpen(false);
      setRecurringMembershipDialogOpen(false);
      setPaymentDialogOpen(false);
      setPaymentItemType('product');
      setMessageDialogOpen(false);
      setVersionHistoryOpen(false);
      setVersionHistoryItemType('');
      setVersionHistoryItemId('');
    }
  }, [open]);

  // New action handlers
  const handleAddCard = (visit: Visit) => {
    setSelectedVisit(visit);
    setAddCardDialogOpen(true);
  };

  const handleManageNotes = (visit: Visit) => {
    setSelectedVisit(visit);
    setNotesDialogOpen(true);
  };

  const handleManageRecurringMembership = (visit: Visit) => {
    setSelectedVisit(visit);
    setRecurringMembershipDialogOpen(true);
  };

  const handleAssignPackage = (visit: Visit) => {
    setSelectedVisit(visit);
    setPaymentItemType('package');
    setPaymentDialogOpen(true);
  };

  const handleChargeProduct = (visit: Visit) => {
    setSelectedVisit(visit);
    setPaymentItemType('product');
    setPaymentDialogOpen(true);
  };

  // Open version history modal
  const handleViewVersionHistory = (itemType: string, itemId: Identifier) => {
    setVersionHistoryItemType(itemType);
    setVersionHistoryItemId(itemId);
    setVersionHistoryOpen(true);
  };

  // Get initial filter params for PaymentDialog based on appointment/visit context
  // Matches VisitsTable.tsx getInitialFilterParams pattern
  const getPaymentFilterParams = (): Record<string, any> | undefined => {
    if (!appointment) return undefined;

    const filters: Record<string, any> = {};

    // Filter by service
    if (appointment.serviceId) {
      filters.services = String(appointment.serviceId);
    }

    // Filter by staff (trainers)
    const trainerIds =
      appointment.primaryTrainerIds && appointment.primaryTrainerIds.length > 0
        ? appointment.primaryTrainerIds
        : appointment.trainerIds;
    if (trainerIds && trainerIds.length > 0) {
      filters.staff = trainerIds.map((id) => String(id)).join(',');
    }

    // Filter by location
    if (appointment.locationId) {
      filters.packageLocation = String(appointment.locationId);
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  // Cancel/Un-cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async ({
      cancel,
      disableEmail,
      cancelAll,
    }: {
      cancel: boolean;
      disableEmail?: boolean;
      cancelAll?: boolean;
    }) => {
      if (!appointment) throw new Error('No appointment');
      const payload: Partial<Appointment> & {
        cancelAll?: boolean;
        disableEmail?: boolean;
      } = {
        id: appointment.id,
        status: cancel
          ? AppointmentStatus.Canceled
          : AppointmentStatus.Scheduled,
      };
      if (disableEmail !== undefined) {
        payload.disableEmail = disableEmail;
      }
      if (cancelAll !== undefined) {
        payload.cancelAll = cancelAll;
      }
      return updateAppointment(payload);
    },
    onSuccess: (_, { cancel }) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success(
        cancel ? t('sessionCancelled') : t('sessionUnCancelled')
      );
      onRefresh?.();
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('errorUpdatingSession')));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!appointment) throw new Error('No appointment');
      return deleteAppointment({ id: appointment.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success(t('sessionDeleted'));
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('errorDeletingSession')));
    },
  });

  // Add to waitlist mutation - v2 modal-new-appointment.js lines 1619-1642
  const addToWaitlistMutation = useMutation({
    mutationFn: async (client: ClientSearchResult) => {
      if (!appointment) throw new Error('No appointment');
      // ClientSearchResult has `name` (full name), split into first/last
      const nameParts = (client.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      return createAppointmentWaitListEntry({
        appointment,
        waitListEntry: {
          userId: client.id,
          firstName,
          lastName,
          email: client.email,
          phoneNumber: client.phoneNumber,
        },
      });
    },
    onSuccess: (newEntry) => {
      // Update local appointment state with new entry
      if (appointment && onAppointmentUpdate) {
        onAppointmentUpdate({
          ...appointment,
          entries: [...(appointment.entries || []), newEntry],
        });
      }
      toast.success(t('addedToWaitlist'));
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('failedAddToWaitlist')));
    },
  });

  // Remove from waitlist mutation - v2 modal-new-appointment.js removeEntry action
  const removeFromWaitlistMutation = useMutation({
    mutationFn: async (entry: WaitListEntry) => {
      return deleteWaitListEntry(entry);
    },
    onSuccess: (_, deletedEntry) => {
      // Update local appointment state by removing entry
      if (appointment && onAppointmentUpdate) {
        onAppointmentUpdate({
          ...appointment,
          entries: (appointment.entries || []).filter(
            (e) => e.id !== deletedEntry.id
          ),
        });
      }
      toast.success(t('removedFromWaitlist'));
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('failedRemoveFromWaitlist')));
    },
  });

  // Handle cancel/un-cancel action - v2 lines 1188-1270
  const handleCancelAppointment = async () => {
    if (!appointment) return;

    const isCancelled = appointment.status === 'cancelled';

    if (isCancelled) {
      // Un-cancel flow - simple confirm - v2 lines 383-408
      const confirmed = await confirm({
        title: t('unCancelSession'),
        message: t('confirmUnCancel'),
        confirmText: t('unCancelSession'),
        confirmColor: 'primary',
      });
      if (confirmed) {
        cancelMutation.mutate({ cancel: false });
      }
    } else if (isRecurring) {
      // Recurring cancel flow - show scope dialog first - v2 lines 1193-1248
      setCancelScopeDialogOpen(true);
    } else {
      // Non-recurring cancel flow - simple confirm then notify - v2 lines 1249-1270
      const confirmed = await confirm({
        title: t('cancelSession'),
        message: t('confirmCancel'),
        confirmText: t('cancelSession'),
        confirmColor: 'error',
      });
      if (confirmed) {
        setCancelNotifyDialogOpen(true);
      }
    }
  };

  // Handle cancel scope selection - v2 lines 1197-1246
  const handleCancelScopeSelect = (scope: RecurringCancelScope) => {
    setCancelScope(scope);
    setCancelScopeDialogOpen(false);
    // After selecting scope, show notify dialog
    setCancelNotifyDialogOpen(true);
  };

  // Handle cancel notify selection and execute cancel - v2 lines 1202-1243
  const handleCancelNotifySelect = (notify: boolean) => {
    setCancelNotifyDialogOpen(false);
    cancelMutation.mutate({
      cancel: true,
      disableEmail: !notify,
      cancelAll: cancelScope === 'all',
    });
    setCancelScope('this'); // Reset for next cancel
  };

  // Handle delete action
  const handleDeleteAppointment = async () => {
    const confirmed = await confirm({
      title: t('deleteSession'),
      message: t('confirmDelete'),
      confirmText: tCommon('buttons.delete'),
      confirmColor: 'error',
    });

    if (confirmed) {
      deleteMutation.mutate();
    }
  };

  // Handle edit action
  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
  };

  // Handle edit save success
  const handleEditSaveSuccess = (_updatedAppointment: Appointment) => {
    setIsEditDialogOpen(false);
    onRefresh?.();
  };

  // Add a client as a pending visit
  // Eligibility is checked by VisitUserInfo/PendingVisitRow components
  const handleAddClient = (client: ClientSearchResult) => {
    if (!appointment) return;

    // Check if user is already in the session (v2 lines 1588-1595)
    const existingVisit = appointment.visits?.find(
      (v) => v.userId === client.id
    );
    if (existingVisit) {
      toast.error(t('alreadyInSession'));
      return;
    }

    // Check if already in pending list
    const existingPending = pendingVisits.find((p) => p.userId === client.id);
    if (existingPending) {
      toast.error(t('alreadyPending'));
      return;
    }

    // Create pending visit (v2 lines 1597-1611)
    const newPending: PendingVisit = {
      id: `pending-${Date.now()}`,
      userId: client.id,
      userName: client.name || '',
      client,
      isSaving: false,
    };

    setPendingVisits((prev) => [newPending, ...prev]);
  };

  // Remove a pending visit without saving (v2 "Remove from Session" action)
  const handleRemovePending = (pendingId: string) => {
    setPendingVisits((prev) => prev.filter((p) => p.id !== pendingId));
  };

  // Mutation to save a pending visit
  const savePendingMutation = useMutation({
    mutationFn: async (pending: PendingVisit) => {
      if (!appointment) throw new Error('No appointment');

      return createAppointmentVisit({
        appointment,
        visit: {
          userId: pending.userId,
          userName: pending.userName,
          status: VisitStatus.Reserved,
          ...(pending.waitlistEntryId && {
            waitlistEntryId: pending.waitlistEntryId,
          }),
        },
      });
    },
    onMutate: (pending) => {
      // Mark as saving
      setPendingVisits((prev) =>
        prev.map((p) => (p.id === pending.id ? { ...p, isSaving: true } : p))
      );
    },
    onSuccess: (_, pending) => {
      // Remove from pending and refresh
      setPendingVisits((prev) => prev.filter((p) => p.id !== pending.id));
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(t('addedToSession', { name: pending.userName }));
      onRefresh?.();
    },
    onError: (error, pending) => {
      // Reset saving state
      setPendingVisits((prev) =>
        prev.map((p) => (p.id === pending.id ? { ...p, isSaving: false } : p))
      );
      toast.error(api.formatError(error, t('errorAddingToSession')));
    },
  });

  // Save a pending visit (v2 "Save to this session only" action)
  const handleSavePending = (pendingId: string) => {
    const pending = pendingVisits.find((p) => p.id === pendingId);
    if (pending) {
      savePendingMutation.mutate(pending);
    }
  };

  // Mutation to save pending visit and then open recurring membership dialog
  const savePendingToAllRecurringMutation = useMutation({
    mutationFn: async (pending: PendingVisit) => {
      if (!appointment) throw new Error('No appointment');

      return createAppointmentVisit({
        appointment,
        visit: {
          userId: pending.userId,
          userName: pending.userName,
          status: VisitStatus.Reserved,
          ...(pending.waitlistEntryId && {
            waitlistEntryId: pending.waitlistEntryId,
          }),
        },
      });
    },
    onMutate: (pending) => {
      setPendingVisits((prev) =>
        prev.map((p) => (p.id === pending.id ? { ...p, isSaving: true } : p))
      );
    },
    onSuccess: (newVisit, pending) => {
      // Remove from pending
      setPendingVisits((prev) => prev.filter((p) => p.id !== pending.id));
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(t('addedToSession', { name: pending.userName }));
      onRefresh?.();

      // Open recurring membership dialog for the new visit
      setSelectedVisit(newVisit);
      setRecurringMembershipDialogOpen(true);
    },
    onError: (error, pending) => {
      setPendingVisits((prev) =>
        prev.map((p) => (p.id === pending.id ? { ...p, isSaving: false } : p))
      );
      toast.error(api.formatError(error, t('errorAddingToSession')));
    },
  });

  // Save a pending visit and open recurring membership dialog
  const handleSavePendingToAllRecurring = (pending: PendingVisit) => {
    savePendingToAllRecurringMutation.mutate(pending);
  };

  // Mutation to create a visit directly from a waitlist entry (v2 lines 1521-1567)
  // Used when entry has no userId (guest flow) - creates visit directly without pending step
  const addGuestFromWaitlistMutation = useMutation({
    mutationFn: async (entry: WaitListEntry) => {
      if (!appointment) throw new Error('No appointment');

      return createAppointmentVisit({
        appointment,
        visit: {
          userName: `${entry.firstName || ''} ${entry.lastName || ''}`.trim(),
          status: VisitStatus.Reserved,
          waitlistEntryId: entry.id,
        },
      });
    },
    onSuccess: (_, entry) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(
        t('addedToSession', { name: `${entry.firstName || ''} ${entry.lastName || ''}`.trim() })
      );
      onRefresh?.();
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('errorAddingToSession')));
    },
  });

  // Handle adding a waitlist entry to the session (v2 addToSession action, lines 1521-1567)
  const handleAddWaitlistToSession = async (entry: WaitListEntry) => {
    if (!appointment) return;

    const name = `${entry.firstName || ''} ${entry.lastName || ''}`.trim();

    // Confirm with user (v2 lines 1525-1527)
    const confirmed = await confirm({
      title: t('addToSession'),
      message: t('confirmAddToSession', { name }),
    });

    if (!confirmed) return;

    if (entry.userId) {
      // Entry has a user account - use pending visit flow (v2 lines 1530-1541)
      // Check if user is already in the session
      const existingVisit = appointment.visits?.find(
        (v) => v.userId === entry.userId
      );
      if (existingVisit) {
        toast.error(t('alreadyInSession'));
        return;
      }

      // Check if already in pending list
      const existingPending = pendingVisits.find(
        (p) => p.userId === entry.userId
      );
      if (existingPending) {
        toast.error(t('alreadyPending'));
        return;
      }

      // Create pending visit with waitlistEntryId
      const newPending: PendingVisit = {
        id: `pending-${Date.now()}`,
        userId: entry.userId,
        userName: name,
        client: {
          id: entry.userId,
          name,
          email: entry.email || '',
        } as ClientSearchResult,
        isSaving: false,
        waitlistEntryId: entry.id,
      };

      setPendingVisits((prev) => [...prev, newPending]);
      toast.success(t('addedAsPending', { name }));
    } else {
      // Entry has no user account (guest) - create visit directly (v2 lines 1543-1562)
      addGuestFromWaitlistMutation.mutate(entry);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={t('loading')}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <Loader size="large" />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            {t('loadingSessionDetails')}
          </Typography>
        </Box>
      </Dialog>
    );
  }

  if (!appointment) return null;

  // Use proper v2 permission logic from usePermissionCheck
  const canManage = canManageAppointment(appointment);

  const isCancelled = appointment.status === 'cancelled';
  // Note: isRecurring is already defined at the top of the component
  // Check if service has waitlist enabled - service object must be populated from API
  const serviceHasWaitlist = Boolean(appointment.service?.waitlist);
  const waitlistCount = appointment.entries?.length || 0;
  const activeVisitsCount =
    appointment.visits?.filter(
      (v) => v.status !== 'cancelled' && v.status !== 'late_cancelled'
    ).length || 0;

  const title = appointment.serviceName || 'Session Details';

  const hasZoomMeeting = !!appointment.zoomMeeting?.joinUrl;

  // Header actions as icon buttons
  const headerActions = canManage ? (
    <Stack direction="row" spacing={1}>
      {/* Zoom - only show when meeting exists */}
      {hasZoomMeeting && (
        <Tooltip title={t('joinZoomMeeting')}>
          <IconButton
            size="small"
            component="a"
            href={appointment.zoomMeeting.joinUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon icon={faVideo} />
          </IconButton>
        </Tooltip>
      )}
      {/* Version History */}
      {isRecurring ? (
        <DropdownMenu
          trigger={
            <Tooltip title={t('viewHistory')}>
              <IconButton size="small">
                <Icon icon={faHistory} />
              </IconButton>
            </Tooltip>
          }
        >
          <ListItem
            primary={t('viewRecurringHistory')}
            onClick={() => handleViewVersionHistory('Fbm::ScheduledAppointment', appointment.scheduledAppointmentId)}
          />
          <ListItem
            primary={t('viewSessionHistory')}
            onClick={() => handleViewVersionHistory('Fbm::Appointment', appointment.id)}
          />
        </DropdownMenu>
      ) : (
        <Tooltip title={t('viewHistory')}>
          <IconButton
            size="small"
            onClick={() => handleViewVersionHistory('Fbm::Appointment', appointment.id)}
          >
            <Icon icon={faHistory} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('editSession')}>
        <IconButton
          size="small"
          onClick={handleEdit}
        >
          <Icon icon={faPenToSquare} />
        </IconButton>
      </Tooltip>
      {canCancelVisit && (
        <Tooltip title={isCancelled ? t('unCancelSession') : t('cancelSession')}>
          <IconButton
            size="small"
            onClick={handleCancelAppointment}
            sx={{ color: isCancelled ? 'success.main' : undefined }}
            disabled={cancelMutation.isPending}
          >
            {isCancelled ? (
              <Icon icon={faCircleCheck} />
            ) : (
              <Icon icon={faBan} />
            )}
          </IconButton>
        </Tooltip>
      )}
      {canCancelVisit && !isRecurring && (
        <Tooltip title={t('deleteSession')}>
          <IconButton
            size="small"
            onClick={handleDeleteAppointment}
            sx={{ color: 'error.main' }}
            disabled={deleteMutation.isPending}
          >
            <Icon icon={faTrash} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  ) : undefined;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={title}
        maxWidth="md"
        fullWidth
        rightAdornment={headerActions}
      >
        <Stack spacing={2}>
          {/* Session Summary */}
          <SessionSummary
            appointment={appointment}
            onAppointmentUpdate={onAppointmentUpdate}
          />

          <Divider />

          {/* Visits/Waitlist Tabs + Bulk Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
            >
              <Tab
                label={`Visits (${activeVisitsCount + pendingVisits.length})`}
              />
              {serviceHasWaitlist && (
                <Tab label={`Waitlist (${waitlistCount})`} />
              )}
            </Tabs>
            {canManage && activeTab === 0 && (
              <BulkActions
                appointment={appointment}
                isLoading={visitActions.isUpdating}
                onBulkConfirmGoing={visitActions.handleBulkConfirmGoing}
                onBulkComplete={visitActions.handleBulkComplete}
                onBulkCancel={visitActions.handleBulkCancel}
                onBulkLateCancelNoFee={visitActions.handleBulkLateCancelNoFee}
                onBulkNoShowNoFee={visitActions.handleBulkNoShowNoFee}
                onBulkResetStatus={visitActions.handleBulkResetStatus}
                onBulkChargeDefaultPackage={visitActions.handleBulkChargeDefaultPackage}
                onMessageAll={messageAllUsers.length > 0 ? handleMessageAll : undefined}
              />
            )}
          </Box>

          {activeTab === 0 && (
            <VisitsSection
              appointment={appointment}
              canManage={canManage}
              pendingVisits={pendingVisits}
              onAddClient={handleAddClient}
              onSavePending={handleSavePending}
              onSavePendingToAllRecurring={handleSavePendingToAllRecurring}
              onRemovePending={handleRemovePending}
              isRecurring={isRecurring}
              isLoading={visitActions.isUpdating}
              // Single visit actions
              onMarkCompleted={visitActions.handleMarkCompleted}
              onMarkNoShow={visitActions.handleMarkNoShow}
              onMarkCancelled={visitActions.handleMarkCancelled}
              onMarkLateCancelled={visitActions.handleMarkLateCancelled}
              onResetStatus={visitActions.handleResetStatus}
              onDontUsePackage={visitActions.handleDontUsePackage}
              onAttemptPackageDeduction={
                visitActions.handleAttemptPackageDeduction
              }
              onChargeDefaultPackage={
                visitActions.handleChargeDefaultPackageAndComplete
              }
              onDeleteVisit={visitActions.handleDeleteVisit}
              // New actions
              onAddCard={handleAddCard}
              onManageNotes={handleManageNotes}
              onManageRecurringMembership={handleManageRecurringMembership}
              onAssignPackage={handleAssignPackage}
              onChargeProduct={handleChargeProduct}
            />
          )}

          {activeTab === 1 && serviceHasWaitlist && (
            <WaitlistSection
              appointment={appointment}
              canManage={canManage}
              onAddToWaitlist={(client) => {
                addToWaitlistMutation.mutate(client);
              }}
              onAddToSession={handleAddWaitlistToSession}
              onRemoveEntry={(entry) => {
                removeFromWaitlistMutation.mutate(entry);
              }}
            />
          )}
        </Stack>
      </Dialog>

      {/* Edit Event Dialog */}
      <EditEventDialog
        open={isEditDialogOpen}
        onClose={handleEditDialogClose}
        appointment={appointment}
        onSaveSuccess={handleEditSaveSuccess}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialogProps} />

      {/* Cancel Scope Dialog - v2 lines 1194-1247: "Only this session" vs "All future sessions" */}
      <ConfirmDialog
        open={cancelScopeDialogOpen}
        onClose={() => setCancelScopeDialogOpen(false)}
        title={t('cancelRecurring')}
        actions={[
          {
            label: t('onlyThisSession'),
            onClick: () => handleCancelScopeSelect('this'),
            variant: 'outlined',
          },
          {
            label: t('allFutureSessions'),
            onClick: () => handleCancelScopeSelect('all'),
            variant: 'contained',
            color: 'error',
          },
        ]}
      >
        {t('cancelRecurringMessage')}
      </ConfirmDialog>

      {/* Cancel Notify Dialog - v2 lines 1202-1218, 1227-1243: "Notify attendees?" */}
      <ConfirmDialog
        open={cancelNotifyDialogOpen}
        title={t('notifyAttendees')}
        onClose={() => setCancelNotifyDialogOpen(false)}
        actions={[
          {
            label: tCommon('buttons.no'),
            onClick: () => handleCancelNotifySelect(false),
            variant: 'outlined',
          },
          {
            label: tCommon('buttons.yes'),
            onClick: () => handleCancelNotifySelect(true),
            variant: 'contained',
            color: 'primary',
          },
        ]}
      >
        {t('notifyAttendeesMessage')}
      </ConfirmDialog>

      {/* Add Card Dialog */}
      <VisitAddCardDialog
        open={addCardDialogOpen}
        onClose={() => {
          setAddCardDialogOpen(false);
          setSelectedVisit(null);
        }}
        visit={selectedVisit}
      />

      {/* Visit Notes Dialog */}
      <VisitNotesDialog
        open={notesDialogOpen}
        onClose={() => {
          setNotesDialogOpen(false);
          setSelectedVisit(null);
        }}
        visit={selectedVisit}
        onVisitUpdate={(updatedVisit) => {
          if (appointment && onAppointmentUpdate) {
            const updatedVisits = (appointment.visits || []).map((v) =>
              v.id === updatedVisit.id ? updatedVisit : v
            );
            onAppointmentUpdate({ ...appointment, visits: updatedVisits });
          }
        }}
      />

      {/* Recurring Membership Dialog */}
      {appointment && (
        <RecurringMembershipDialog
          open={recurringMembershipDialogOpen}
          onClose={() => {
            setRecurringMembershipDialogOpen(false);
            setSelectedVisit(null);
          }}
          visit={selectedVisit}
          appointment={appointment}
          onSuccess={onRefresh}
        />
      )}

      {/* Payment Dialog for Charging Packages/Products */}
      {selectedVisit?.userId && paymentDialogOpen && (
        <PaymentDialog
          open={paymentDialogOpen}
          onClose={() => {
            setPaymentDialogOpen(false);
            setSelectedVisit(null);
          }}
          user={
            selectedVisit.user || ({
              id: selectedVisit.userId,
              firstName: selectedVisit.firstName || selectedVisit.userName?.split(' ')[0] || '',
              lastName: selectedVisit.lastName || selectedVisit.userName?.split(' ').slice(1).join(' ') || '',
              email: selectedVisit.email || '',
              phoneNumber: selectedVisit.phoneNumber || '',
            } as User)
          }
          itemType={paymentItemType}
          onPaymentComplete={() => {
            setPaymentDialogOpen(false);
            setSelectedVisit(null);
            onRefresh?.();
          }}
          initialFilterParams={paymentItemType === 'package' ? getPaymentFilterParams() : undefined}
        />
      )}

      {/* Message All Dialog */}
      <MessageDialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        title={`Send Message to ${messageAllUsers.length} attendee${messageAllUsers.length === 1 ? '' : 's'}`}
        defaultUsers={messageAllUsers}
      />

      {/* Version History Dialog */}
      <VersionHistory
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        itemType={versionHistoryItemType}
        itemId={versionHistoryItemId}
      />
    </>
  );
};
